import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuCheck, LuImage, LuLockKeyhole, LuUpload } from "react-icons/lu";
import "./style.css";
import {
  getCorporation,
  updateBranding,
} from "../../../../../../api/configuration";
import { setAlert } from "../../../../../../reducers/userConfigSlice";

const imageFields = [
  {
    key: "logo",
    title: "Logo principal",
    help: "Se utiliza como imagen principal del negocio.",
  },
  {
    key: "ticketLogo",
    title: "Logo de ticket",
    help: "Se utiliza en los tickets del negocio.",
  },
  {
    key: "favIcon",
    title: "Favicon",
    help: "Icono que aparece en la pestaña del navegador.",
  },
];
const ImageField = ({
  field,
  image,
  file,
  removed,
  onFile,
  onRemove,
  disabled,
}) => {
  const [preview, setPreview] = useState(null),
    [failed, setFailed] = useState(false);
  const input = useRef(null);
  useEffect(() => {
    setFailed(false);
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file, image]);
  const visible = !removed && !failed && (preview || image);
  return (
    <div className="designAsset">
      <label htmlFor={`branding-${field.key}`}>{field.title}</label>
      <div className="designAssetControls">
        <div className="designAssetPreview">
          {visible ? (
            <img
              src={preview || image}
              alt={field.title}
              onError={() => setFailed(true)}
            />
          ) : (
            <LuImage aria-label={`Sin ${field.title}`} />
          )}
        </div>
        <div className="designAssetActions">
          <input
            ref={input}
            id={`branding-${field.key}`}
            aria-label={`Subir ${field.title}`}
            type="file"
            tabIndex={-1}
            accept="image/png,image/jpeg,image/gif,image/webp"
            disabled={disabled}
            className="designFileInput"
            onChange={(e) => onFile(e.target.files[0] || null)}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => input.current?.click()}
          >
            <LuUpload aria-hidden="true" />
            {visible ? "Cambiar imagen" : "Subir imagen"}
            <span className="designSrOnly">: {field.title}</span>
          </button>
          {(image || file || removed) && (
            <button
              type="button"
              className="designRemove"
              disabled={disabled}
              onClick={() => {
                if (input.current) input.current.value = "";
                onRemove();
              }}
            >
              {removed ? `Conservar ${field.title}` : `Eliminar ${field.title}`}
            </button>
          )}
        </div>
      </div>
      <p>{field.help}</p>
      {file && (
        <p className="designSelectedFile">{file.name} · Pendiente de guardar</p>
      )}
      {removed && <p>Se eliminará al guardar.</p>}
    </div>
  );
};
const draftOf = (data) => ({
  color: data.corporation.color || "#2563eb",
  layoutShop: data.corporation.layoutShop || "LIST",
});
const validColor = (value) => /^#[0-9a-f]{6}$/i.test(value);
export const Design = () => {
  const dispatch = useDispatch(),
    role = useSelector((s) => s.aplicationConfig.userConfig.userData?.role),
    canEdit = role === "admin";
  const [data, setData] = useState(null),
    [form, setForm] = useState(null),
    [baseline, setBaseline] = useState(null),
    [files, setFiles] = useState({}),
    [removed, setRemoved] = useState([]),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(false);
  const pending = useRef(false),
    root = useRef(null);
  const dirty =
    !!form &&
    (JSON.stringify(form) !== JSON.stringify(baseline) ||
      Object.values(files).some(Boolean) ||
      removed.length > 0);
  const read = async () => {
    const response = await getCorporation();
    if (response?.error !== false || !response.data?.corporation)
      throw Error("No se pudo cargar el diseño. Inténtalo de nuevo.");
    return { ...response.data, imageVersion: Date.now() };
  };
  const install = (next) => {
    setData(next);
    const draft = draftOf(next);
    setForm(draft);
    setBaseline(draft);
  };
  const query = async () => {
    try {
      setError("");
      install(await read());
    } catch (e) {
      setError(e.message);
    }
  };
  useEffect(() => {
    let current = true;
    read()
      .then((next) => {
        if (current) install(next);
      })
      .catch((e) => {
        if (current) setError(e.message);
      });
    return () => {
      current = false;
    };
  }, []);
  useEffect(() => {
    const warn = (e) => {
      if (dirty || saving) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, saving]);
  const clearFiles = () => {
    setFiles({});
    setRemoved([]);
    root.current?.querySelectorAll('input[type="file"]').forEach((input) => {
      input.value = "";
    });
  };
  const change = (key, value) => {
    if (!canEdit || pending.current) return;
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };
  const selectFile = (key, file) => {
    if (!canEdit || pending.current) return;
    if (
      file &&
      (!["image/png", "image/jpeg", "image/gif", "image/webp"].includes(
        file.type,
      ) ||
        file.size > 5 * 1024 * 1024)
    ) {
      setError("Selecciona una imagen PNG, JPG, GIF o WebP de hasta 5 MB.");
      const input = root.current.querySelector(`#branding-${key}`);
      if (input) input.value = "";
      return;
    }
    setFiles((previous) => ({ ...previous, [key]: file }));
    setRemoved((previous) => previous.filter((item) => item !== key));
    setError("");
    setSaved(false);
  };
  const removeFile = (key) => {
    if (!canEdit || pending.current) return;
    setFiles((previous) => ({ ...previous, [key]: null }));
    setRemoved((previous) =>
      previous.includes(key)
        ? previous.filter((item) => item !== key)
        : [...previous, key],
    );
    setSaved(false);
  };
  const reset = () => {
    if (pending.current) return;
    setForm(baseline);
    clearFiles();
    setError("");
    setSaved(false);
  };
  const save = async (e) => {
    e.preventDefault();
    if (!canEdit || pending.current || !dirty) return;
    if (!validColor(form.color)) {
      setError("Escribe un color hexadecimal válido, por ejemplo #2563EB.");
      root.current.querySelector("#design-color-hex")?.focus();
      return;
    }
    if (!["LIST", "GALLERY"].includes(form.layoutShop)) {
      setError("Selecciona Lista o Galería.");
      return;
    }
    pending.current = true;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const imageKeys = Object.keys(files).filter((key) => files[key]);
      const color =
        form.color === baseline.color ? data.corporation.color : form.color;
      const layoutShop =
        form.layoutShop === baseline.layoutShop
          ? data.corporation.layoutShop
          : form.layoutShop;
      const response = await updateBranding({
        color,
        layoutShop,
        files: imageKeys.map((key) => files[key]),
        imageKeys: JSON.stringify(imageKeys),
        imagesToDelete: JSON.stringify(removed),
      });
      if (response?.error !== false)
        throw Error(
          response?.message ||
            "No se pudo confirmar el guardado. El borrador se conserva.",
        );
      const next = await read();
      if (
        (next.corporation.color || "").toLowerCase() !==
          (color || "").toLowerCase() ||
        (next.corporation.layoutShop || "") !== (layoutShop || "")
      )
        throw Error(
          "La configuración leída no coincide con los cambios. El borrador se conserva.",
        );
      install(next);
      clearFiles();
      setSaved(true);
      dispatch(setAlert("Configuración de diseño actualizada con éxito"));
    } catch (e) {
      setError(
        e.message ||
          "No se pudo confirmar el guardado. El borrador se conserva.",
      );
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };
  const renderImage = (field) => (
    <ImageField
      key={field.key}
      field={field}
      image={
        data[field.key]
          ? `${data[field.key]}${data[field.key].includes("?") ? "&" : "?"}v=${data.imageVersion}`
          : null
      }
      file={files[field.key]}
      removed={removed.includes(field.key)}
      disabled={!canEdit || saving}
      onFile={(file) => selectFile(field.key, file)}
      onRemove={() => removeFile(field.key)}
    />
  );
  return (
    <section className="designReference" ref={root} aria-busy={saving}>
      {!canEdit && (
        <p className="designReadOnly">
          <LuLockKeyhole aria-hidden="true" />
          Solo lectura: tu rol no permite modificar el diseño.
        </p>
      )}
      {!data ? (
        <div className="designLoadState">
          {error ? (
            <>
              <p role="alert">{error}</p>
              <button type="button" onClick={query}>
                Reintentar
              </button>
            </>
          ) : (
            <p role="status">Cargando diseño…</p>
          )}
        </div>
      ) : (
        <form className="designCard" onSubmit={save} noValidate>
          <header>
            <h3>Diseño de la tienda</h3>
            <p>La identidad visual del negocio y el aspecto del catálogo.</p>
          </header>
          <fieldset disabled={!canEdit || saving} className="designBody">
            <h4>Identidad visual</h4>
            <p>
              Los logos y el color de marca que se usan en la tienda y en los
              tickets.
            </p>
            <div className="designAssets">
              {imageFields.slice(0, 2).map(renderImage)}
            </div>
            <div className="designColorField">
              <label htmlFor="design-color-hex">Color primario</label>
              <div className="designColorControls">
                <input
                  id="colorPicker"
                  type="color"
                  aria-label="Seleccionar color primario"
                  value={validColor(form.color) ? form.color : "#2563eb"}
                  onChange={(e) => change("color", e.target.value)}
                />
                <input
                  id="design-color-hex"
                  type="text"
                  maxLength={7}
                  autoComplete="off"
                  spellCheck={false}
                  value={form.color.toUpperCase()}
                  onChange={(e) => change("color", e.target.value)}
                  aria-describedby="design-color-help"
                />
                <div
                  className="designColorPreview"
                  style={{
                    backgroundColor: validColor(form.color)
                      ? form.color
                      : "transparent",
                  }}
                  aria-label="Vista previa del color primario"
                />
              </div>
              <p id="design-color-help">
                Se usa en los elementos destacados de la tienda.
              </p>
            </div>
            <h4 id="design-appearance-title">Apariencia de la tienda</h4>
            <p>Cómo se organizan los productos en el catálogo.</p>
            <div
              className="designLayouts"
              role="radiogroup"
              aria-labelledby="design-appearance-title"
            >
              {[
                {
                  value: "LIST",
                  title: "Lista",
                  text: "Vista con productos organizados en formato lista.",
                },
                {
                  value: "GALLERY",
                  title: "Galería",
                  text: "Vista con productos organizados en formato galería.",
                },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`designLayout ${form.layoutShop === option.value ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="shop-layout"
                    value={option.value}
                    checked={form.layoutShop === option.value}
                    onChange={() => change("layoutShop", option.value)}
                  />
                  <div className="designLayoutTitle">
                    <strong>{option.title}</strong>
                    <span className="designLayoutCheck">
                      {form.layoutShop === option.value && (
                        <LuCheck aria-hidden="true" />
                      )}
                    </span>
                  </div>
                  <p>{option.text}</p>
                  <div
                    className={`designLayoutExample ${option.value === "LIST" ? "designExampleList" : "designExampleGallery"}`}
                    aria-hidden="true"
                  >
                    {[1, 2, 3].map((n) => (
                      <div key={n}>
                        <span />
                        <i />
                        <i />
                      </div>
                    ))}
                  </div>
                </label>
              ))}
            </div>
            <details className="designAdvanced">
              <summary>Más opciones</summary>
              {renderImage(imageFields[2])}
            </details>
          </fieldset>
          {error && (
            <p className="designError" role="alert">
              {error}
            </p>
          )}
          <footer>
            <span role="status">
              {saving
                ? "Guardando…"
                : error
                  ? "Revisa los cambios"
                  : dirty
                    ? "Cambios sin guardar"
                    : saved
                      ? "Diseño guardado"
                      : "Todo guardado"}
            </span>
            {dirty && canEdit && (
              <button type="button" disabled={saving} onClick={reset}>
                Descartar cambios
              </button>
            )}
            <button
              type="submit"
              className="designSave"
              disabled={!canEdit || saving || !dirty}
            >
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </footer>
        </form>
      )}
    </section>
  );
};
